import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../partage/api/client';
import type { Boucle } from '../../partage/api/types';
import { relireApres } from '../../partage/relectures';

/**
 * **Le refus d'une boucle, et ses deux issues qui touchent un lien posé** : le retirer, ou le
 * remplacer par une bulle. Dans les deux cas le cercle est ouvert, et on pose ENSUITE le lien
 * qu'on essayait de poser (`reposer`) : c'est lui qu'on voulait. S'il referme une autre boucle,
 * le refus revient avec elle.
 *
 * Écrit une fois pour ceux qui posent un « attend » : « + Condition » dans la fiche
 * (`PoserUneCondition`) et « Lier » sur la carte (chantier liens-entre-bulles, ticket 04).
 * `montrer` ouvre le refus (`RefusDeCycle`), `fermer` le ferme sans rien poser.
 */
export function useSortieDeBoucle(reposer: (tente: Boucle['tente']) => void) {
  const client = useQueryClient();
  const [boucle, setBoucle] = useState<Boucle | null>(null);

  const sortir = useMutation({
    mutationFn: async (issue: { lienId: string; titre?: string }) => {
      if (issue.titre) await api.inserer(issue.lienId, issue.titre);
      else await api.delier(issue.lienId);
    },
    onSuccess: () => {
      void relireApres(client, 'poserOuRetirerUneCondition');
      if (boucle) reposer(boucle.tente);
    },
  });

  return {
    boucle,
    montrer: (b: Boucle) => setBoucle(b),
    /** Ce qui ferme le refus quand le lien tenté est posé. */
    oublier: () => setBoucle(null),
    sortir,
    fermer: () => {
      setBoucle(null);
      sortir.reset();
    },
  };
}
